import React, { useCallback, useContext, useEffect, useState } from "react";
import activeCountries from "./activeCountries";
import FixedLoader from "./components/FixedLoader";
import CountryConfigContext from "./CountryConfigContext";
import CountryContext, {
  LANGUAGE_KEY,
  SELECTED_COUNTRY_KEY,
} from "./CountryContext";
import Button from "./ui-components/Button";
import Modal from "./ui-components/Modal";
import axios from "axios";
import { API_URL } from "./utils/constants";
import getLocale from "./api/getLocale";
import convertLocale from "./utils/convertLocale";
import CountryNotAvailable from "./ui-components/pages/CountryNotAvailable";

const CountrySelection = ({ children }) => {
  const { country, setCountry } = useContext(CountryContext);
  const { config, setConfig } = useContext(CountryConfigContext);
  const [error, setError] = useState(false);

  const onClickCountry = useCallback(
    ({ code, defaultLanguage }) => {
      localStorage.setItem(SELECTED_COUNTRY_KEY, code);
      localStorage.setItem(LANGUAGE_KEY, defaultLanguage);
      setCountry(activeCountries.find((c) => c.code === code));
    },
    [setCountry]
  );

  // if a country code hasn't been selected yet but the provided url contains a country code, automatically select the
  // correspondant country
  const currentPath = window.location.pathname;
  const isCountryPath = activeCountries.some(({ code }) => currentPath.startsWith('/' + code));
  const urlCode = isCountryPath ? currentPath.slice(1, 3) : null;
  const isLocalStorageIncomplete = [SELECTED_COUNTRY_KEY, LANGUAGE_KEY]
    .some((k) => localStorage.getItem(k) === null);
  // reset the default language for the current country if
  // - the local storage is incomplete (i.e. some keys are missing)
  // - the selected language is not available for the current country
  const resetDefaultLanguage = isLocalStorageIncomplete || (
    !!config
    && !config.languages.some(({ code }) => code === localStorage.getItem(LANGUAGE_KEY))
  );
  if (isCountryPath && resetDefaultLanguage) {
    const {
      code,
      defaultLanguage,
    } = activeCountries.find(({ code }) => code === urlCode);
    onClickCountry({code, defaultLanguage});
  }

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const [locale, services, languages] = await Promise.all([
          getLocale(country),
          axios.get(
            `/${country.code}${API_URL}/services/all_extended?active=true&asc=order`
          ),
          axios.get(`/${country.code}${API_URL}/country_languages/all`),
          // axios.get(`/${country.code}${API_URL}/products/all`),
        ]);

        if (
          languages.data.length &&
          languages.data
            .map((l) => l.code)
            .indexOf(localStorage.getItem(LANGUAGE_KEY)) < 0
        ) {
          localStorage.removeItem(SELECTED_COUNTRY_KEY);
          localStorage.removeItem(LANGUAGE_KEY);
          window.location = "/";
          return;
        }

        setConfig({
          locale: convertLocale(locale.data),
          services: services.data,
          languages: languages.data,
          products: {},
        });
      } catch (error) {
        console.log(error);
        setError(true);
      }
    };

    if (country && !config) {
      fetchConfig();
    } else if (country === undefined) {
      window.location = "/";
      localStorage.removeItem(SELECTED_COUNTRY_KEY);
      localStorage.removeItem(LANGUAGE_KEY);
      setCountry(undefined)
    }
  }, [country, config, setConfig, setCountry]);

  if (error) {
    return <CountryNotAvailable country={country} setCountry={setCountry} />;
  }

  if (!country && country !== undefined) {
    return (
      <div className="fixed w-full h-full left-0 top-0 bg-white">
        <Modal fixed>
          <h1>Select your country</h1>
          <div className="flex">
            {activeCountries.map(({ code, name, ...rest }) => (
              <Button
                onClick={() => onClickCountry({ code, ...rest })}
                className="mr-4 mt-4"
                key={code}
              >
                {name}
              </Button>
            ))}
          </div>
        </Modal>
      </div>
    );
  }

  if (country?.code && config) {
    return children;
  }

  return <FixedLoader />;
};

export default CountrySelection;
